// import external libraries
import { useTranslation } from 'react-i18next';

// import components
import { TextField } from '@mui/material';
import PlanUnitMultiSelect from 'components/shared/PlanUnitMultiSelect';

// import utils
import { renderFilterTags } from '../../BacklogFilters.utils';
import { useGetOptionLabel } from 'components/shared/PlanUnitSelector/PlanUnitSelector.hooks';

// import types
import type { AutocompleteRenderInputParams } from '@mui/material';
import type { BacklogPlanUnitFilterProps } from './BacklogPlanUnitFilter.types';

/**
 * The plan unit (planeenheid) filter of the employee view.
 *
 * Built on the shared PlanUnitMultiSelect, so the options come from the plan units the app already loads
 * at boot — no separate call for the takenlijst — and the list draws the same grouped, checkboxed options a
 * planner sees everywhere else it picks plan units.
 *
 * Not sent to the backend yet: the backlog request has no field for it, so this narrows nothing on its own.
 * What it does do is scope the employee view — the takenlijst asks nothing until a plan unit is picked — and
 * feed the employee and function lists beside it, which are that plan unit's own.
 */
const BacklogPlanUnitFilter = ({
  selected,
  onChange,
  required
}: BacklogPlanUnitFilterProps) => {
  'use memo';

  const { t } = useTranslation('common');
  // The label the options themselves are drawn with, so the field summarises a pick in the same words the
  // planner ticked — including the name-short and code the account's plan unit display preference asks for.
  const getOptionLabel = useGetOptionLabel();

  return (
    <PlanUnitMultiSelect
      // Its own input rather than the shared component's label prop, which renders a TextField with nothing
      // else on it: the asterisk and the error state are what say the employee view cannot go on without a
      // plan unit, the same way the care receiver field says it in the patient view.
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          label={t('planUnit')}
          required={required}
          error={required && selected.length === 0}
        />
      )}
      selectedOptions={selected}
      onChange={onChange}
      // A plan group and a plan unit each carry their own table's `id`, so those ids collide: on the default
      // `id` the field reads plan group 5 and plan unit 5 as the same option and ticks both. `uniqId` is
      // what tells them apart — a number for a unit, "5-group" for a group.
      idProperty="uniqId"
      // Emptied in one go rather than unticked value by value — see BacklogActivityFilter. Clearing it is
      // allowed even though the employee view requires one: the page then says what it needs, as it does
      // before the first pick.
      disableClearable={false}
      // MultiSelect draws nothing at all for a selection unless it is told how (its renderTags defaults to
      // the empty string, with limitTags at 0), so without this the planner ticks a plan unit and the field
      // still looks empty. Summarised the same way as the filters beside it: one pick named, more counted.
      renderTags={renderFilterTags(getOptionLabel, t('selected').toLowerCase())}
      fullWidth
    />
  );
};

export default BacklogPlanUnitFilter;
